import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import ClearIcon from "@material-ui/icons/Clear";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  ECABIN,
  USER_GROUPS,
  checkRequiredPermissions,
  findValueInText,
  formatTimeWithTZOffset,
  getLCLocation,
  getLCState,
  getLatestLifeCycleState,
  getToken,
  getWarehouses,
  lifeCycleStates as lifeCycleStateOptions,
  requestErrorHandler,
  tableAlternateColors,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";
import SubAssemblyDialog from "./SubAssemblyDialog";

const server = EnvSettings.server;

const createDefaultLegacySubAssembly = () => ({
  number: "",
  model: "",
  color: "",
});

const createDefaultQuickFilters = () => ({
  lcStates: [],
  lcWarehouses: [],
  onlyAssigned: false,
  onlyUnassigned: false,
});

const matchesText = (data, value) =>
  findValueInText(
    data === undefined || data === null ? "" : data.toString(),
    value
  );

const getAssemblyNoteText = (assembly) => {
  const noteTexts = [];

  const pushNotes = (notes) => {
    if (!Array.isArray(notes)) {
      return;
    }
    notes.forEach((note) => {
      if (note && note.text) {
        noteTexts.push(note.text);
      }
    });
  };

  pushNotes(assembly.ManufacturingEntry && assembly.ManufacturingEntry.Notes);
  if (Array.isArray(assembly.LifeCycleStates)) {
    assembly.LifeCycleStates.forEach((state) => {
      pushNotes(state && state.Notes);
    });
  }
  if (assembly.Device && Array.isArray(assembly.Device.LifeCycleStates)) {
    assembly.Device.LifeCycleStates.forEach((state) => {
      pushNotes(state && state.Notes);
    });
  }

  return noteTexts.join(" ");
};

export default function SerialisedSubAssemblies() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [serialisedSubAssemblies, setSerialisedSubAssemblies] = useState([]);
  const [filteredSerialisedSubAssemblies, setFilteredSerialisedSubAssemblies] =
    useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [quickFilters, setQuickFilters] = useState(createDefaultQuickFilters);
  const [showQuickFilters, setShowQuickFilters] = useState(false);
  const [quickLcStateOpen, setQuickLcStateOpen] = useState(false);
  const [quickWarehouseOpen, setQuickWarehouseOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warehouseLocation, setWarehouseLocation] = useState();
  const [selectedSubAssembly, setSelectedSubAssembly] = useState(null);
  const [selectedSubAssemblyId, setSelectedSubAssemblyId] = useState(null);
  const [showLegacySubAssemblyDialog, setShowLegacySubAssemblyDialog] =
    useState(false);
  const canCreateLegacySubAssembly = checkRequiredPermissions([
    USER_GROUPS.ADMIN,
    USER_GROUPS.PRODUCTION,
  ]);

  const fetchSerialisedSubAssemblies = useCallback(() => {
    setIsLoading(true);
    setErrorMessage("");
    fetch(server + "/get_serialised_subassemblies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setSerialisedSubAssemblies([]);
          setErrorMessage(
            result.message ||
              t(
                "manufacturing.serialisedSubAssembliesFetchFailed",
                "Failed to fetch serialised sub-assemblies."
              )
          );
        } else {
          setSerialisedSubAssemblies(
            Array.isArray(result.serialisedSubAssemblies)
              ? result.serialisedSubAssemblies
              : []
          );
          setErrorMessage("");
        }
      })
      .catch(() => {
        setSerialisedSubAssemblies([]);
        setErrorMessage(
          t(
            "manufacturing.serialisedSubAssembliesFetchFailed",
            "Failed to fetch serialised sub-assemblies."
          )
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, t]);

  useEffect(() => {
    fetchSerialisedSubAssemblies();
  }, [fetchSerialisedSubAssemblies]);

  useEffect(() => {
    getWarehouses(setWarehouseLocation, dispatch);
  }, [dispatch]);

  const processedAssemblies = useMemo(() => {
    return serialisedSubAssemblies.map((assembly) => {
      const deviceState = getLatestLifeCycleState(
        assembly.Device?.LifeCycleStates
      );
      const ownState = getLatestLifeCycleState(assembly.LifeCycleStates);
      const resolvedState = deviceState || ownState;
      return {
        ...assembly,
        manufacturedAt:
          assembly.ManufacturingEntry?.createdAt || assembly.createdAt || null,
        currentLCState: resolvedState?.state
          ? getLCState(resolvedState.state)
          : null,
        currentLCStateValue: resolvedState?.state || null,
        currentLocation: resolvedState?.location
          ? getLCLocation(resolvedState.location)
          : null,
        currentLocationValue: resolvedState?.location || null,
        noteText: getAssemblyNoteText(assembly),
      };
    });
  }, [serialisedSubAssemblies]);

  useEffect(() => {
    if (!selectedSubAssemblyId) {
      return;
    }
    const updatedAssembly = processedAssemblies.find(
      (assembly) => assembly.id === selectedSubAssemblyId
    );
    if (updatedAssembly) {
      setSelectedSubAssembly(updatedAssembly);
    } else {
      setSelectedSubAssembly(null);
      setSelectedSubAssemblyId(null);
    }
  }, [processedAssemblies, selectedSubAssemblyId]);

  const hasActiveQuickFilters = useMemo(
    () =>
      Object.values(quickFilters).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value)
      ),
    [quickFilters]
  );

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    const selectedLcStates = quickFilters.lcStates || [];
    const selectedWarehouses = quickFilters.lcWarehouses || [];

    const filtered = processedAssemblies.filter((assembly) => {
      const fields = [
        assembly.number,
        assembly.model,
        assembly.color,
        assembly.ManufacturingEntry?.uuid,
        assembly.ManufacturingEntry?.batchNumber,
        assembly.ManufacturingEntry?.StockItem?.sku,
        assembly.ManufacturingEntry?.StockItem?.name,
        assembly.ManufacturingEntry?.StockChange?.Warehouse?.name,
        assembly.Device?.serialNumber,
        assembly.manufacturedAt,
        assembly.currentLCState,
        assembly.currentLocation,
        assembly.noteText,
      ];
      const matchesSearch =
        !term ||
        fields.some((field) => {
          if (field === undefined || field === null) {
            return false;
          }
          return field.toString().toLowerCase().includes(term);
        });
      const stockItemText = [
        assembly.ManufacturingEntry?.StockItem?.sku,
        assembly.ManufacturingEntry?.StockItem?.name,
      ]
        .filter(Boolean)
        .join(" ");
      const matchesLcState =
        !selectedLcStates.length ||
        selectedLcStates.includes(assembly.currentLCStateValue);
      const matchesWarehouse =
        !selectedWarehouses.length ||
        selectedWarehouses.includes(assembly.currentLocationValue);
      const matchesAssignment = quickFilters.onlyAssigned
        ? !!assembly.Device?.id
        : quickFilters.onlyUnassigned
        ? !assembly.Device?.id
        : true;

      return (
        matchesSearch &&
        matchesText(assembly.number, quickFilters.chassisNumber) &&
        matchesText(assembly.model, quickFilters.model) &&
        matchesText(assembly.color, quickFilters.color) &&
        matchesText(stockItemText, quickFilters.stockItem) &&
        matchesText(
          assembly.ManufacturingEntry?.batchNumber,
          quickFilters.batchNumber
        ) &&
        matchesText(assembly.Device?.serialNumber, quickFilters.device) &&
        matchesText(assembly.noteText, quickFilters.note) &&
        matchesLcState &&
        matchesWarehouse &&
        matchesAssignment
      );
    });
    setFilteredSerialisedSubAssemblies(filtered);
  }, [processedAssemblies, searchValue, quickFilters]);

  const headCells = useMemo(
    () => [
      {
        id: "manufacturedAt",
        label: t("manufacturing.manufacturedAt", "Manufactured at"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "number",
        label: t("serialisedSubAssembly.number", "Chassis number"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "model",
        label: t("serialisedSubAssembly.model", "Model"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "color",
        label: t("serialisedSubAssembly.color", "Color"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingEntry.StockItem.sku",
        label: t(
          "manufacturing.resultingStockItem",
          "Resulting stock item (SKU)"
        ),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingEntry.batchNumber",
        label: t("manufacturing.batchNumber", "Batch number"),
        disablePadding: false,
        numeric: false,
      },

      {
        id: "Device.serialNumber",
        label: t("devices.serialNumber", "Assigned device"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "currentLCState",
        label: t("deviceList.lcState"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "currentLocation",
        label: t("location", "Location"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "noteText",
        label: t("note"),
        disablePadding: false,
        numeric: false,
      },
    ],
    [t]
  );

  const specialFormatting = useMemo(
    () => [
      {
        id: "manufacturedAt",
        format: (data) => (data ? formatTimeWithTZOffset(data, ECABIN) : ""),
      },
    ],
    []
  );

  return (
    <Grid container spacing={3}>
      <LegacySubAssemblyDialog
        open={showLegacySubAssemblyDialog}
        onClose={() => setShowLegacySubAssemblyDialog(false)}
        onCreated={fetchSerialisedSubAssemblies}
      />
      <SubAssemblyDialog
        subAssembly={selectedSubAssembly}
        onClose={() => {
          setSelectedSubAssembly(null);
          setSelectedSubAssemblyId(null);
        }}
        onRefresh={fetchSerialisedSubAssemblies}
      />
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/manufacturing">
            {t("manufacturing.header", "Manufacturing")}
          </Link>
          <Typography color="textPrimary">
            {t(
              "manufacturing.serialisedSubAssemblies",
              "Serialised sub-assemblies"
            )}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Title>
                {t(
                  "manufacturing.serialisedSubAssemblies",
                  "Serialised sub-assemblies"
                )}
              </Title>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("common.search", "Search")}
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </Grid>
            {canCreateLegacySubAssembly ? (
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowLegacySubAssemblyDialog(true)}
                >
                  {t(
                    "manufacturing.addLegacySerialisedSubAssembly",
                    "Add legacy sub-assembly"
                  )}
                </Button>
              </Grid>
            ) : null}
            <Grid item container spacing={1} xs={12} alignItems="center">
              <Grid item>
                <Title>{t("quickFilters")}</Title>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => setShowQuickFilters(!showQuickFilters)}
                >
                  {showQuickFilters ? t("hide") : t("show")}
                </Button>
              </Grid>
              {hasActiveQuickFilters ? (
                <Grid item>
                  <Button
                    color="secondary"
                    onClick={() =>
                      setQuickFilters(createDefaultQuickFilters())
                    }
                  >
                    {t("clearAll")}
                  </Button>
                </Grid>
              ) : null}
            </Grid>
            {showQuickFilters ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("serialisedSubAssembly.number", "Chassis number")}
                    variant="outlined"
                    value={quickFilters.chassisNumber || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        chassisNumber: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("serialisedSubAssembly.model", "Model")}
                    variant="outlined"
                    value={quickFilters.model || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        model: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("serialisedSubAssembly.color", "Color")}
                    variant="outlined"
                    value={quickFilters.color || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        color: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("manufacturing.stockItem", "Stock item")}
                    variant="outlined"
                    value={quickFilters.stockItem || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        stockItem: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("manufacturing.batchNumber", "Batch number")}
                    variant="outlined"
                    value={quickFilters.batchNumber || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        batchNumber: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("manufacturing.assignedDevice", "Assigned device")}
                    variant="outlined"
                    value={quickFilters.device || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        device: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item container xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("deviceList.lcState")}
                    variant="outlined"
                    select
                    SelectProps={{
                      multiple: true,
                      open: quickLcStateOpen,
                      onOpen: () => setQuickLcStateOpen(true),
                      onClose: () => setQuickLcStateOpen(false),
                      value: quickFilters.lcStates,
                      renderValue: () => "",
                    }}
                    onChange={(event) => {
                      setQuickFilters({
                        ...quickFilters,
                        lcStates: event.target.value,
                      });
                      setQuickLcStateOpen(false);
                    }}
                  >
                    {lifeCycleStateOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {quickFilters.lcStates?.length ? (
                    <Grid item xs={12}>
                      {quickFilters.lcStates.map((state) => (
                        <Button
                          key={state}
                          size="small"
                          endIcon={<ClearIcon fontSize="small" />}
                          onClick={() =>
                            setQuickFilters({
                              ...quickFilters,
                              lcStates: quickFilters.lcStates.filter(
                                (value) => value !== state
                              ),
                            })
                          }
                        >
                          {getLCState(state) || state}
                        </Button>
                      ))}
                    </Grid>
                  ) : null}
                </Grid>
                <Grid item container xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("deviceList.lcLocation")}
                    variant="outlined"
                    select
                    SelectProps={{
                      multiple: true,
                      open: quickWarehouseOpen,
                      onOpen: () => setQuickWarehouseOpen(true),
                      onClose: () => setQuickWarehouseOpen(false),
                      value: quickFilters.lcWarehouses,
                      renderValue: () => "",
                    }}
                    onChange={(event) => {
                      setQuickFilters({
                        ...quickFilters,
                        lcWarehouses: event.target.value,
                      });
                      setQuickWarehouseOpen(false);
                    }}
                  >
                    {warehouseLocation?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {quickFilters.lcWarehouses?.length ? (
                    <Grid item xs={12}>
                      {quickFilters.lcWarehouses.map((warehouse) => {
                        const match = warehouseLocation?.find(
                          (item) => item.value === warehouse
                        );
                        return (
                          <Button
                            key={warehouse}
                            size="small"
                            endIcon={<ClearIcon fontSize="small" />}
                            onClick={() =>
                              setQuickFilters({
                                ...quickFilters,
                                lcWarehouses:
                                  quickFilters.lcWarehouses.filter(
                                    (value) => value !== warehouse
                                  ),
                              })
                            }
                          >
                            {match ? match.label : warehouse}
                          </Button>
                        );
                      })}
                    </Grid>
                  ) : null}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t("note")}
                    variant="outlined"
                    value={quickFilters.note || ""}
                    onChange={(event) =>
                      setQuickFilters({
                        ...quickFilters,
                        note: event.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={quickFilters.onlyAssigned}
                        onChange={(event, checked) =>
                          setQuickFilters({
                            ...quickFilters,
                            onlyAssigned: checked,
                            onlyUnassigned: checked
                              ? false
                              : quickFilters.onlyUnassigned,
                          })
                        }
                      />
                    }
                    label={t(
                      "manufacturing.onlyAssignedSubAssemblies",
                      "Assigned to device"
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={quickFilters.onlyUnassigned}
                        onChange={(event, checked) =>
                          setQuickFilters({
                            ...quickFilters,
                            onlyUnassigned: checked,
                            onlyAssigned: checked
                              ? false
                              : quickFilters.onlyAssigned,
                          })
                        }
                      />
                    }
                    label={t(
                      "manufacturing.onlyUnassignedSubAssemblies",
                      "Unassigned"
                    )}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} container spacing={1}>
              <Grid item>
                <Typography color="primary">
                  {t(
                    "manufacturing.fetchedSerialisedSubAssemblies",
                    "Fetched sub-assemblies"
                  )}
                </Typography>
                <Typography>{processedAssemblies.length}</Typography>
              </Grid>
              <Grid item>
                <Typography color="primary">
                  {t(
                    "manufacturing.listedSerialisedSubAssemblies",
                    "Listed sub-assemblies"
                  )}
                </Typography>
                <Typography>
                  {filteredSerialisedSubAssemblies.length}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {errorMessage ? (
            <Grid container style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{ padding: 16, background: "#ffeeee" }}
                >
                  {errorMessage}
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {isLoading ? (
            <CircularProgress />
          ) : filteredSerialisedSubAssemblies.length ? (
            <SortableTable
              tableLayout="fixed"
              downloadButton
              header={t(
                "manufacturing.serialisedSubAssemblies",
                "Serialised sub-assemblies"
              )}
              headCells={headCells}
              tableData={filteredSerialisedSubAssemblies}
              rowsPerPage={25}
              defaultSortRow="manufacturedAt"
              alternateColors
              getRowStyle={tableAlternateColors}
              specialDataFormatting={specialFormatting}
              handleRowClick={(subAssembly) => {
                setSelectedSubAssembly(subAssembly);
                setSelectedSubAssemblyId(subAssembly?.id ?? null);
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              {searchValue || hasActiveQuickFilters
                ? t(
                    "manufacturing.noSerialisedSubAssembliesMatchSearch",
                    "No sub-assemblies match your search."
                  )
                : t(
                    "manufacturing.noSerialisedSubAssembliesFound",
                    "No serialised sub-assemblies available."
                  )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

const LegacySubAssemblyDialog = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState(
    createDefaultLegacySubAssembly()
  );
  const [warehouses, setWarehouses] = useState([]);
  const [producedAt, setProducedAt] = useState("");
  const [stateValue, setStateValue] = useState("storaged");
  const [locationValue, setLocationValue] = useState("");
  const [qualityValue, setQualityValue] = useState(1);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const canCreate = checkRequiredPermissions([
    USER_GROUPS.ADMIN,
    USER_GROUPS.PRODUCTION,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormValues(createDefaultLegacySubAssembly());
    setProducedAt("");
    setStateValue("storaged");
    setLocationValue("");
    setQualityValue(1);
    setNote("");
    getWarehouses((items) => setWarehouses(items || []), dispatch);
  }, [dispatch, open]);

  useEffect(() => {
    if (!open || locationValue || !warehouses.length) {
      return;
    }
    setLocationValue(warehouses[0].value || warehouses[0].name || "");
  }, [locationValue, open, warehouses]);

  const requiresClientLocation =
    stateValue === "demo" || stateValue === "dispatched";
  const computedLocation =
    requiresClientLocation
      ? "client"
      : stateValue === "missing"
      ? "unknown"
      : locationValue;

  const handleClose = () => {
    if (!isSaving && typeof onClose === "function") {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (!canCreate) {
      return;
    }
    if (!formValues.number.trim()) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.serialisedSubAssemblyNumberRequired",
            "Chassis number is required."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!producedAt) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectProducedAt",
            "Select the production date."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!stateValue) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectLifeCycleState",
            "Select a life cycle state."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!computedLocation) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectLifeCycleLocation",
            "Select a location for the life cycle state."
          ),
          severity: "warning",
        })
      );
      return;
    }

    setIsSaving(true);
    fetch(server + "/add_serialised_subassembly", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        serialisedSubassembly: {
          number: formValues.number.trim(),
          model: formValues.model.trim() || undefined,
          color: formValues.color || undefined,
        },
        producedAt,
        lifecyclestate: {
          state: stateValue,
          location: computedLocation,
          quality: qualityValue || null,
        },
        note: note.trim() || undefined,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          return;
        }
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.serialisedSubAssemblyCreated",
              "Serialised sub-assembly created."
            ),
            severity: "success",
          })
        );
        if (typeof onCreated === "function") {
          onCreated(result.serialisedSubAssembly);
        }
        if (typeof onClose === "function") {
          onClose();
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.serialisedSubAssemblyCreateFailed",
              "Failed to create serialised sub-assembly."
            ),
            severity: "error",
          })
        );
      })
      .finally(() => setIsSaving(false));
  };

  if (!canCreate) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t(
          "manufacturing.addLegacySerialisedSubAssembly",
          "Add legacy sub-assembly"
        )}
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {t(
              "manufacturing.legacySerialisedSubAssemblyHint",
              "Use this for sub-assemblies produced before manufacturing entries were tracked. No manufacturing entry or stock change will be created."
            )}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              size="small"
              label={t("serialisedSubAssembly.number", "Chassis number")}
              value={formValues.number}
              onChange={(event) =>
                setFormValues({
                  ...formValues,
                  number: event.target.value,
                })
              }
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              label={t("manufacturing.manufacturedAt", "Manufactured at")}
              value={producedAt}
              onChange={(event) => setProducedAt(event.target.value)}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={t("serialisedSubAssembly.model", "Model")}
              value={formValues.model}
              onChange={(event) =>
                setFormValues({
                  ...formValues,
                  model: event.target.value,
                })
              }
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label={t("serialisedSubAssembly.color", "Color")}
              value={formValues.color}
              onChange={(event) =>
                setFormValues({
                  ...formValues,
                  color: event.target.value,
                })
              }
              disabled={isSaving}
            >
              <MenuItem value="">
                <em>{t("common.notAvailable", "N/A")}</em>
              </MenuItem>
              <MenuItem value="Grey">Grey</MenuItem>
              <MenuItem value="White">White</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              required
              variant="outlined"
              size="small"
              label={t("deviceList.lcState", "Life cycle state")}
              value={stateValue}
              onChange={(event) => setStateValue(event.target.value)}
              disabled={isSaving}
            >
              {lifeCycleStateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            {requiresClientLocation ? (
              <TextField
                fullWidth
                required
                size="small"
                variant="outlined"
                label={t("deviceList.lcLocation", "Location")}
                value={t("manufacturing.clientLocation", "Client")}
                disabled
              />
            ) : stateValue === "missing" ? (
              <TextField
                fullWidth
                required
                size="small"
                variant="outlined"
                label={t("deviceList.lcLocation", "Location")}
                value={t("manufacturing.unknownLocation", "Unknown")}
                disabled
              />
            ) : (
              <TextField
                select
                fullWidth
                required
                variant="outlined"
                size="small"
                label={t("deviceList.lcLocation", "Location")}
                value={locationValue}
                onChange={(event) => setLocationValue(event.target.value)}
                disabled={isSaving}
              >
                {warehouses.map((option) => (
                  <MenuItem
                    key={option.id || option.value || option.name}
                    value={option.value || option.name}
                  >
                    {option.label || option.name || option.value}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label={t("lifeCycleManagement.quality", "Quality")}
              value={qualityValue}
              onChange={(event) => setQualityValue(Number(event.target.value))}
              disabled={isSaving}
            >
              <MenuItem value={1}>1. Class - A</MenuItem>
              <MenuItem value={2}>2. Class - B</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={t("note", "Note")}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              rows={3}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t("cancel", "Cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? t("common.saving", "Saving...") : t("create", "Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
